import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LookupTableData } from '../types/model.types';
import { Plus, Trash2 } from 'lucide-react';

interface LookupTableEditorProps {
  data: LookupTableData;
  rowDimension: string;
  colDimension: string;
  onChange: (data: LookupTableData) => void;
  disabled?: boolean;
}

export function LookupTableEditor({
  data,
  rowDimension,
  colDimension,
  onChange,
  disabled,
}: LookupTableEditorProps) {
  const handleAddRow = () => {
    const newRows = [...data.rows, `Fila ${data.rows.length + 1}`];
    const newValues = [...data.values, new Array(data.cols.length).fill(0)];
    onChange({ ...data, rows: newRows, values: newValues });
  };

  const handleAddCol = () => {
    const newCols = [...data.cols, `Col ${data.cols.length + 1}`];
    const newValues = data.values.map((row) => [...row, 0]);
    onChange({ ...data, cols: newCols, values: newValues });
  };

  const handleRemoveRow = (index: number) => {
    const newRows = data.rows.filter((_, i) => i !== index);
    const newValues = data.values.filter((_, i) => i !== index);
    onChange({ ...data, rows: newRows, values: newValues });
  };

  const handleRemoveCol = (index: number) => {
    const newCols = data.cols.filter((_, i) => i !== index);
    const newValues = data.values.map((row) => row.filter((_, i) => i !== index));
    onChange({ ...data, cols: newCols, values: newValues });
  };

  const handleRowLabelChange = (index: number, label: string) => {
    const newRows = [...data.rows];
    newRows[index] = label;
    onChange({ ...data, rows: newRows });
  };

  const handleColLabelChange = (index: number, label: string) => {
    const newCols = [...data.cols];
    newCols[index] = label;
    onChange({ ...data, cols: newCols });
  };

  const handleValueChange = (rowIndex: number, colIndex: number, value: number) => {
    const newValues = data.values.map((row, ri) =>
      ri === rowIndex ? row.map((v, ci) => (ci === colIndex ? value : v)) : [...row]
    );
    onChange({ ...data, values: newValues });
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddRow}
          disabled={disabled}
        >
          <Plus className="w-3 h-3 mr-1" />
          Fila ({rowDimension})
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddCol}
          disabled={disabled}
        >
          <Plus className="w-3 h-3 mr-1" />
          Columna ({colDimension})
        </Button>
      </div>

      {data.rows.length > 0 && data.cols.length > 0 && (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="p-2 text-left font-medium min-w-[120px]">
                  {rowDimension} \ {colDimension}
                </th>
                {data.cols.map((col, ci) => (
                  <th key={ci} className="p-1 min-w-[100px]">
                    <div className="flex items-center gap-1">
                      <Input
                        value={col}
                        onChange={(e) => handleColLabelChange(ci, e.target.value)}
                        className="h-7 text-xs"
                        disabled={disabled}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => handleRemoveCol(ci)}
                        disabled={disabled}
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, ri) => (
                <tr key={ri} className="border-b last:border-0">
                  <td className="p-1">
                    <div className="flex items-center gap-1">
                      <Input
                        value={row}
                        onChange={(e) => handleRowLabelChange(ri, e.target.value)}
                        className="h-7 text-xs font-medium"
                        disabled={disabled}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => handleRemoveRow(ri)}
                        disabled={disabled}
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </td>
                  {data.cols.map((_, ci) => (
                    <td key={ci} className="p-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={data.values[ri]?.[ci] ?? 0}
                        onChange={(e) => handleValueChange(ri, ci, parseFloat(e.target.value) || 0)}
                        className="h-7 text-xs text-right"
                        disabled={disabled}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data.rows.length === 0 || data.cols.length === 0) && (
        <p className="text-xs text-muted-foreground">
          Agrega filas y columnas para definir la matriz de precios.
        </p>
      )}
    </div>
  );
}

export default LookupTableEditor;
